import { useState, useMemo, useEffect } from 'react';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X, Plus, Link2, Unlink, ChevronDown, ChevronRight, GripVertical, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TimePeriodSelector } from './TimePeriodSelector';
import { MetricChipSelector } from './MetricChipSelector';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import type { CompareState, CompareSlot, DatePreset, TimeGranularity, MetricKey, PreviousDateSettings } from '@/types/reports';
import type { Group, Company, IndustryType } from '@/types';

// Helper to convert hex color to rgba with alpha
function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (result) {
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `rgba(0, 0, 0, ${alpha})`;
}

interface CompareSidebarProps {
  compareState: CompareState;
  onCompareStateChange: (state: CompareState) => void;
  slots: CompareSlot[];
  onAddSlot: () => void;
  onRemoveSlot: (slotId: string) => void;
  onReorderSlots: (slots: CompareSlot[]) => void;
  onClearAll?: () => void;
  groups: Group[];
  companies: Company[];
  selectedIndustryTypes: IndustryType[];
  groupLabel: string;
  /** Whether to show the onboarding tooltip on the first empty slot */
  showOnboarding?: boolean;
  /** Callback to dismiss onboarding tooltip */
  onDismissOnboarding?: () => void;
}

// Sortable slot item component
interface SortableSlotItemProps {
  slot: CompareSlot;
  index: number;
  openSlotId: string | null;
  setOpenSlotId: (id: string | null) => void;
  onRemove: () => void;
  onGroupsChange: (groupIds: string[]) => void;
  getSlotLabel: (slot: CompareSlot) => string;
  groupLabel: string;
  selectedIndustryTypes: IndustryType[];
}

function SortableSlotItem({
  slot,
  index: _index,
  openSlotId,
  setOpenSlotId,
  onRemove,
  onGroupsChange,
  getSlotLabel,
  groupLabel: _groupLabel,
  selectedIndustryTypes,
}: SortableSlotItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slot.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      initial={{ opacity: 0, scale: 0.95, y: -10 }}
      animate={{ opacity: isDragging ? 0.5 : 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: -10 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      layout
      className="rounded-md border border-border"
      {...attributes}
    >
      <div
        className="flex items-center gap-2 p-2 bg-muted/20"
        style={{ borderLeftColor: slot.color, borderLeftWidth: '4px' }}
      >
        {/* Drag handle */}
        <div
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors p-1"
        >
          <GripVertical className="h-4 w-4" />
        </div>

        <Popover open={openSlotId === slot.id} onOpenChange={(open) => setOpenSlotId(open ? slot.id : null)}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                "flex-1 flex items-center gap-2 text-left min-w-0 hover:bg-muted/50 rounded px-2 py-1 transition-colors",
                slot.groupIds.length === 0 && "text-muted-foreground"
              )}
            >
              <span className="text-sm flex-1 truncate">
                {getSlotLabel(slot)}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0 z-50 rounded-md overflow-hidden" align="start" sideOffset={4}>
            <CompanyGroupFilters
              selectedGroupIds={slot.groupIds}
              onGroupsChange={onGroupsChange}
              selectedIndustryTypes={selectedIndustryTypes}
              embeddedMode
            />
          </PopoverContent>
        </Popover>
        <button
          onClick={onRemove}
          className="p-1 rounded hover:bg-muted transition-colors"
          aria-label="Remove comparison"
        >
          <X className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </div>
    </motion.div>
  );
}

export function CompareSidebar({
  compareState,
  onCompareStateChange,
  slots,
  onAddSlot,
  onRemoveSlot,
  onReorderSlots,
  onClearAll,
  groups,
  companies,
  selectedIndustryTypes,
  groupLabel,
  showOnboarding = false,
  onDismissOnboarding,
}: CompareSidebarProps) {
  const {
    activeSections,
    linkedDateRange,
    globalDatePreset,
    globalCustomDateRange,
    globalGranularity,
  } = compareState;

  // Track which slot selector is open
  const [openSlotId, setOpenSlotId] = useState<string | null>(null);

  // Collapsible section states - all open by default
  const [slotsExpanded, setSlotsExpanded] = useState(true);
  const [dateRangeExpanded, setDateRangeExpanded] = useState(true);
  const [metricsExpanded, setMetricsExpanded] = useState(true);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-dismiss onboarding tooltip after 60 seconds
  useEffect(() => {
    if (!showOnboarding || !onDismissOnboarding) return;
    const timer = setTimeout(() => {
      onDismissOnboarding();
    }, 60000);
    return () => clearTimeout(timer);
  }, [showOnboarding, onDismissOnboarding]);

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = slots.findIndex((slot) => slot.id === active.id);
      const newIndex = slots.findIndex((slot) => slot.id === over.id);
      const newSlots = arrayMove(slots, oldIndex, newIndex);
      onReorderSlots(newSlots);
    }
  };

  // Determine if film industry is selected (for production dates checkbox visibility)
  const isFilmIndustry = useMemo(() => {
    if (slots.length === 0) return selectedIndustryTypes.includes('film');
    // Check if any slot has film groups
    return slots.some(slot => {
      const slotGroups = groups.filter(g => slot.groupIds.includes(g.id));
      const companyIds = [...new Set(slotGroups.map(g => g.company_id))];
      return companies.some(c => companyIds.includes(c.id) && c.industry === 'film');
    });
  }, [slots, groups, companies, selectedIndustryTypes]);

  // Get production date info for linked mode
  // Returns date range string based on production dates, supporting multiple productions per slot
  const productionDatesInfo = useMemo(() => {
    if (slots.length === 0) return { dateRange: null, canUse: false };

    // Helper to parse ISO date string as local date
    const parseLocalDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };

    // For each slot, find all root groups and calculate their combined date range
    const slotDates = slots.map(slot => {
      if (slot.groupIds.length === 0) return null;

      // Find root groups (no parent) among selected
      const selectedSet = new Set(slot.groupIds);
      const rootGroups = slot.groupIds
        .map(id => groups.find(g => g.id === id))
        .filter((g): g is Group => !!g)
        .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

      if (rootGroups.length === 0) return null;

      // Calculate combined date range from all root groups
      let earliestStart: string | null = null;
      let latestEnd: string | null = null;

      rootGroups.forEach(g => {
        if (g.start_date) {
          if (!earliestStart || g.start_date < earliestStart) {
            earliestStart = g.start_date;
          }
        }
        if (g.end_date) {
          if (!latestEnd || g.end_date > latestEnd) {
            latestEnd = g.end_date;
          }
        }
      });

      // Need at least start and end dates from any root groups
      if (!earliestStart || !latestEnd) return null;

      return {
        start: earliestStart,
        end: latestEnd,
        name: rootGroups.length === 1 ? rootGroups[0].name : `${rootGroups.length} productions`
      };
    });

    // Check if all slots have valid dates
    const allHaveDates = slotDates.every(d => d !== null);
    if (!allHaveDates) {
      return { dateRange: null, canUse: false };
    }

    // Check if all slots have the same dates (for unified checkbox)
    const firstDates = slotDates[0];
    const allSameDates = slotDates.every(d =>
      d?.start === firstDates?.start && d?.end === firstDates?.end
    );

    if (allSameDates && firstDates) {
      try {
        const start = parseLocalDate(firstDates.start);
        const end = parseLocalDate(firstDates.end);
        return {
          dateRange: `${format(start, 'MMM d')} → ${format(end, 'MMM d, yyyy')}`,
          canUse: true
        };
      } catch {
        return { dateRange: null, canUse: false };
      }
    }

    // Different dates per slot - can still use but show generic message
    return { dateRange: 'Per-slot dates', canUse: true };
  }, [slots, groups]);

  // Show production dates checkbox in linked mode for film industry
  const showProductionDatesCheckbox = isFilmIndustry;

  // Handle linked date range toggle
  const handleLinkedDateRangeChange = (linked: boolean) => {
    onCompareStateChange({
      ...compareState,
      linkedDateRange: linked,
    });
  };

  // Handle global date preset change (linked mode)
  const handleGlobalDatePresetChange = (preset: DatePreset) => {
    onCompareStateChange({
      ...compareState,
      globalDatePreset: preset,
      globalCustomDateRange: { start: null, end: null },
    });
  };

  // Handle global custom date range change (linked mode)
  const handleGlobalCustomDateRangeChange = (start: Date | null, end: Date | null) => {
    onCompareStateChange({
      ...compareState,
      globalDatePreset: 'custom',
      globalCustomDateRange: { start, end },
    });
  };

  // Handle global granularity change (linked mode)
  const handleGlobalGranularityChange = (granularity: TimeGranularity) => {
    onCompareStateChange({
      ...compareState,
      globalGranularity: granularity,
    });
  };

  // Handle use full group toggle (for linked mode - affects all slots)
  const handleUseFullGroupChange = (useFullGroup: boolean) => {
    // Apply to all slots when in linked mode
    const updatedSlots = slots.map(slot => ({
      ...slot,
      useFullGroup,
    }));
    onCompareStateChange({
      ...compareState,
      slots: updatedSlots,
    });
  };

  // Handle enabled metrics change
  const handleMetricsChange = (metrics: MetricKey[]) => {
    onCompareStateChange({
      ...compareState,
      enabledMetrics: metrics,
    });
  };

  // Handle group selection change for a slot
  const handleSlotGroupsChange = (slotId: string, groupIds: string[]) => {
    // Get smart label
    let label = '';
    if (groupIds.length === 1) {
      const group = groups.find(g => g.id === groupIds[0]);
      label = group?.name || '';
    } else if (groupIds.length > 1) {
      // Multiple groups selected - find root groups (parents not in selection)
      const selectedSet = new Set(groupIds);
      const rootGroups = groupIds
        .map(id => groups.find(g => g.id === id))
        .filter((g): g is Group => !!g)
        .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

      // If exactly one root group is selected (with its children), use that group's name
      if (rootGroups.length === 1) {
        label = rootGroups[0].name;
      } else {
        // Otherwise, count root groups only
        label = `${rootGroups.length} ${groupLabel}s`;
      }
    }

    const updatedSlots = slots.map(slot =>
      slot.id === slotId
        ? { ...slot, groupIds, label, type: groupIds.length > 1 ? 'group' as const : 'individual' as const }
        : slot
    );
    onCompareStateChange({
      ...compareState,
      slots: updatedSlots,
    });
    // Don't close the popover - let user select multiple groups
  };

  // Get label for a slot - smart labeling that recognizes parent+children selections
  const getSlotLabel = (slot: CompareSlot) => {
    if (slot.label) return slot.label;
    if (slot.groupIds.length === 0) return `Select ${groupLabel}`;
    if (slot.groupIds.length === 1) {
      const group = groups.find(g => g.id === slot.groupIds[0]);
      return group?.name || 'Unknown';
    }

    // Multiple groups selected - find root groups (parents not in selection)
    const selectedSet = new Set(slot.groupIds);
    const rootGroups = slot.groupIds
      .map(id => groups.find(g => g.id === id))
      .filter((g): g is Group => !!g)
      .filter(g => !g.parent_group_id || !selectedSet.has(g.parent_group_id));

    // If exactly one root group is selected (with its children), show that group's name
    if (rootGroups.length === 1) {
      return rootGroups[0].name;
    }

    // Otherwise, count root groups only
    return `${rootGroups.length} ${groupLabel}s`;
  };

  // Check if there are any slots with groups selected (for reset button state)
  const hasActiveComparisons = slots.length > 0 && slots.some(s => s.groupIds.length > 0);

  // Default clear handler if not provided
  const handleClearAll = () => {
    if (onClearAll) {
      onClearAll();
    } else {
      // Default: remove all slots
      onCompareStateChange({
        ...compareState,
        slots: [],
      });
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4 sticky top-0">
      {/* Header with Reset button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Compare Settings
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearAll}
          disabled={!hasActiveComparisons}
          className={cn(
            "text-xs h-7 px-2 transition-colors uppercase",
            hasActiveComparisons
              ? 'text-muted-foreground hover:text-foreground'
              : 'text-muted-foreground/40'
          )}
        >
          <RotateCcw className="h-3 w-3 mr-1" />
          Reset
        </Button>
      </div>

      {/* Slots List - Collapsible with drag-and-drop */}
      <div className="space-y-2">
        <button
          onClick={() => setSlotsExpanded(!slotsExpanded)}
          className="w-full flex items-center justify-between hover:bg-muted/30 rounded px-1 py-0.5 -mx-1 transition-colors"
        >
          <div className="flex items-center gap-1.5">
            {slotsExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wider cursor-pointer">
              Comparing <span className="text-[#8FB560]">{slots.reduce((total, slot) => total + slot.groupIds.length, 0)}</span> {groupLabel}s
            </Label>
          </div>
          <span className="text-xs text-muted-foreground">
            {slots.length === 0 ? '(0/4)' : slots.length === 4 ? '(4/4)' : (<>(<span className="text-[#8FB560]">{slots.length}</span>/4)</>)}
          </span>
        </button>

        {slotsExpanded && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={slots.map(s => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              <AnimatePresence mode="popLayout">
                {slots.map((slot, index) => (
                  <SortableSlotItem
                    key={slot.id}
                    slot={slot}
                    index={index}
                    openSlotId={openSlotId}
                    setOpenSlotId={setOpenSlotId}
                    onRemove={() => onRemoveSlot(slot.id)}
                    onGroupsChange={(groupIds) => handleSlotGroupsChange(slot.id, groupIds)}
                    getSlotLabel={getSlotLabel}
                    groupLabel={groupLabel}
                    selectedIndustryTypes={selectedIndustryTypes}
                  />
                ))}
              </AnimatePresence>

              {/* Empty slots placeholders */}
              <AnimatePresence mode="popLayout">
                {Array.from({ length: 4 - slots.length }).map((_, i) => {
                  const isFirstEmpty = i === 0;
                  const showTooltip = isFirstEmpty && showOnboarding && slots.length === 0;

                  return (
                    <div key={`empty-${i}`} className="relative">
                      <motion.button
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15, delay: i * 0.05 }}
                        layout
                        onClick={() => {
                          if (showOnboarding && onDismissOnboarding) {
                            onDismissOnboarding();
                          }
                          onAddSlot();
                        }}
                        className={cn(
                          "w-full rounded-md border-2 border-dashed p-3 flex items-center justify-center gap-2 transition-all",
                          "text-muted-foreground hover:text-foreground text-sm",
                          showTooltip
                            ? "border-[#8FB560] bg-[#8FB560]/5 ring-2 ring-[#8FB560] ring-offset-2 ring-offset-card shadow-[0_0_12px_rgba(143,181,96,0.4)]"
                            : "border-border hover:border-[#8FB560]/50 hover:bg-muted/20"
                        )}
                      >
                        <Plus className="h-4 w-4" />
                        <span className="text-xs text-muted-foreground">Select {groupLabel}</span>
                      </motion.button>

                      {/* Onboarding tooltip */}
                      {showTooltip && (
                        <div className="absolute z-50 animate-in fade-in duration-300 left-full top-1/2 -translate-y-1/2 ml-3 slide-in-from-left-2">
                          <div className="relative bg-[#8FB560] rounded-md shadow-lg px-3 py-2 whitespace-nowrap">
                            {/* Arrow pointing left */}
                            <div className="absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-[#8FB560]" />
                            <span className="text-sm font-semibold text-black">START HERE</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </AnimatePresence>
            </div>
          </SortableContext>
        </DndContext>
        )}
      </div>

      <div className="border-t border-border" />

      {/* Date Range Controls - Collapsible */}
      <div className="space-y-3">
        <button
          onClick={() => setDateRangeExpanded(!dateRangeExpanded)}
          className="w-full flex items-center justify-between hover:bg-muted/30 rounded px-1 py-0.5 -mx-1 transition-colors"
        >
          <div className="flex items-center gap-1.5">
            {dateRangeExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <Label className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer">
              Date Range
            </Label>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLinkedDateRangeChange(!linkedDateRange);
            }}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors",
              linkedDateRange
                ? "bg-[#8FB560]/10 text-[#8FB560]"
                : "bg-muted/30 text-muted-foreground hover:text-foreground"
            )}
          >
            {linkedDateRange ? (
              <>
                <Link2 className="h-3 w-3" />
                Linked
              </>
            ) : (
              <>
                <Unlink className="h-3 w-3" />
                Per-slot
              </>
            )}
          </button>
        </button>

        {dateRangeExpanded && (
          <>
            {linkedDateRange && (
          <>
            {/* Production Dates Checkbox - only for film industry */}
            {showProductionDatesCheckbox && (
              <label
                htmlFor="compareUseProductionDates"
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border-2 transition-all duration-150",
                  "focus-within:outline-none focus-within:ring-2 focus-within:ring-[#8FB560]/50 focus-within:ring-offset-1 focus-within:ring-offset-background",
                  // Extra muted when no slots have groups
                  slots.length === 0 || slots.every(s => s.groupIds.length === 0) && "opacity-40 cursor-not-allowed",
                  // Normal muted when can't use production dates
                  slots.some(s => s.groupIds.length > 0) && !productionDatesInfo.canUse && "opacity-50 cursor-not-allowed",
                  productionDatesInfo.canUse && slots.some(s => s.groupIds.length > 0) && "cursor-pointer",
                  slots.length > 0 && slots.every(s => s.useFullGroup)
                    ? "bg-[#8FB560]/20 border-[#8FB560] hover:bg-[#8FB560]/25"
                    : "bg-[#8FB560]/10 border-[#8FB560]/60 hover:bg-[#8FB560]/15 hover:border-[#8FB560]"
                )}
              >
                <Checkbox
                  id="compareUseProductionDates"
                  checked={slots.length > 0 && slots.every(s => s.useFullGroup)}
                  onCheckedChange={(checked) => handleUseFullGroupChange(checked === true)}
                  disabled={!productionDatesInfo.canUse || slots.length === 0 || slots.every(s => s.groupIds.length === 0)}
                  className={cn(
                    "h-6 w-6 rounded-md shrink-0",
                    "data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]",
                    "border-2 border-[#8FB560]/60"
                  )}
                />
                <div className="flex-1 text-left min-w-0">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider block truncate">Use Production Dates</span>
                  {productionDatesInfo.dateRange ? (
                    <span className={cn(
                      "block text-xs font-medium mt-0.5 uppercase tracking-wider truncate",
                      slots.length > 0 && slots.every(s => s.useFullGroup) ? "text-[#8FB560]" : "text-foreground/80"
                    )}>{productionDatesInfo.dateRange}</span>
                  ) : slots.length === 0 || slots.every(s => s.groupIds.length === 0) ? (
                    <span className="block text-xs text-muted-foreground/60 mt-0.5 uppercase tracking-wider truncate">Add {groupLabel.toLowerCase()}s first</span>
                  ) : (
                    <span className="block text-xs text-muted-foreground/60 mt-0.5 uppercase tracking-wider truncate">Productions need start/end dates</span>
                  )}
                </div>
              </label>
            )}

            <TimePeriodSelector
              granularity={globalGranularity}
              periodCount={4}
              useFullGroup={slots.length > 0 && slots.every(s => s.useFullGroup)}
              onGranularityChange={handleGlobalGranularityChange}
              onPeriodCountChange={() => {}} // Not used in compare mode
              onUseFullProductionChange={handleUseFullGroupChange}
              datePreset={globalDatePreset}
              customStartDate={globalCustomDateRange.start}
              customEndDate={globalCustomDateRange.end}
              onDatePresetChange={handleGlobalDatePresetChange}
              onCustomDateRangeChange={handleGlobalCustomDateRangeChange}
              disabled={slots.length === 0}
              groupLabel={groupLabel}
            />
          </>
        )}

            {/* Per-slot date pickers when unlinked - card-based visual grouping */}
            {!linkedDateRange && slots.length > 0 && (
              <div className="space-y-4 mt-2">
                {/* Production Dates Checkbox - at top of per-slot list for film industry */}
                {showProductionDatesCheckbox && (
                  <label
                    htmlFor="compareUseProductionDatesPerSlot"
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border-2 transition-all duration-150",
                      "focus-within:outline-none focus-within:ring-2 focus-within:ring-[#8FB560]/50 focus-within:ring-offset-1 focus-within:ring-offset-background",
                      slots.every(s => s.groupIds.length === 0) && "opacity-40 cursor-not-allowed",
                      slots.some(s => s.groupIds.length > 0) && !productionDatesInfo.canUse && "opacity-50 cursor-not-allowed",
                      productionDatesInfo.canUse && slots.some(s => s.groupIds.length > 0) && "cursor-pointer",
                      slots.length > 0 && slots.every(s => s.useFullGroup)
                        ? "bg-[#8FB560]/20 border-[#8FB560] hover:bg-[#8FB560]/25"
                        : "bg-[#8FB560]/10 border-[#8FB560]/60 hover:bg-[#8FB560]/15 hover:border-[#8FB560]"
                    )}
                  >
                    <Checkbox
                      id="compareUseProductionDatesPerSlot"
                      checked={slots.length > 0 && slots.every(s => s.useFullGroup)}
                      onCheckedChange={(checked) => handleUseFullGroupChange(checked === true)}
                      disabled={!productionDatesInfo.canUse || slots.every(s => s.groupIds.length === 0)}
                      className={cn(
                        "h-6 w-6 rounded-md shrink-0",
                        "data-[state=checked]:bg-[#8FB560] data-[state=checked]:border-[#8FB560]",
                        "border-2 border-[#8FB560]/60"
                      )}
                    />
                    <div className="flex-1 text-left min-w-0">
                      <span className="text-xs text-muted-foreground uppercase tracking-wider block truncate">Use Production Dates</span>
                      {productionDatesInfo.dateRange ? (
                        <span className={cn(
                          "block text-xs font-medium mt-0.5 uppercase tracking-wider truncate",
                          slots.length > 0 && slots.every(s => s.useFullGroup) ? "text-[#8FB560]" : "text-foreground/80"
                        )}>{productionDatesInfo.dateRange}</span>
                      ) : slots.every(s => s.groupIds.length === 0) ? (
                        <span className="block text-xs text-muted-foreground/60 mt-0.5 uppercase tracking-wider truncate">Add {groupLabel.toLowerCase()}s first</span>
                      ) : (
                        <span className="block text-xs text-muted-foreground/60 mt-0.5 uppercase tracking-wider truncate">Productions need start/end dates</span>
                      )}
                    </div>
                  </label>
                )}

                {slots.map((slot, index) => {
                  const slotLabel = getSlotLabel(slot);
                  return (
                    <div
                      key={slot.id}
                      className="rounded-lg overflow-hidden transition-all duration-200"
                      style={{
                        backgroundColor: slot.color,
                      }}
                      role="group"
                      aria-label={`Comparison ${index + 1} date selection`}
                    >
                      {/* Slot Header - solid color background with darker text */}
                      <div className="px-3 py-1 flex items-center gap-2">
                        <span
                          className="text-xs font-semibold uppercase tracking-wider truncate flex-1"
                          style={{ color: hexToRgba('#000000', 0.7) }}
                        >
                          {slotLabel || `Select ${groupLabel}`}
                        </span>
                      </div>

                      {/* Date Picker Controls */}
                      <div className="p-3 bg-card/90 rounded-b-md">
                        <TimePeriodSelector
                          granularity={slot.granularity}
                          periodCount={4}
                          useFullGroup={slot.useFullGroup}
                          onGranularityChange={(granularity) => {
                            const updatedSlots = slots.map(s =>
                              s.id === slot.id ? { ...s, granularity } : s
                            );
                            onCompareStateChange({ ...compareState, slots: updatedSlots });
                          }}
                          onPeriodCountChange={() => {}}
                          onUseFullProductionChange={(useFullGroup) => {
                            if (useFullGroup) {
                              // Enabling: Save current slot settings before switching
                              const previousDateSettings: PreviousDateSettings = {
                                datePreset: slot.datePreset || 'last-30-days',
                                customDateRange: slot.customDateRange || { start: null, end: null },
                                granularity: slot.granularity,
                              };
                              const updatedSlots = slots.map(s =>
                                s.id === slot.id ? { ...s, useFullGroup, previousDateSettings } : s
                              );
                              onCompareStateChange({ ...compareState, slots: updatedSlots });
                            } else {
                              // Disabling: Restore previous settings atomically
                              const prev = slot.previousDateSettings;
                              const updatedSlots = slots.map(s => {
                                if (s.id !== slot.id) return s;
                                if (prev) {
                                  return {
                                    ...s,
                                    useFullGroup,
                                    datePreset: prev.datePreset,
                                    dateRangeMode: prev.datePreset === 'custom' ? 'custom' as const : 'preset' as const,
                                    customDateRange: prev.datePreset === 'custom' ? prev.customDateRange : { start: null, end: null },
                                    granularity: prev.granularity,
                                  };
                                }
                                return { ...s, useFullGroup };
                              });
                              onCompareStateChange({ ...compareState, slots: updatedSlots });
                            }
                          }}
                          datePreset={slot.datePreset || 'last-30-days'}
                          customStartDate={slot.customDateRange?.start}
                          customEndDate={slot.customDateRange?.end}
                          onDatePresetChange={(preset) => {
                            const updatedSlots = slots.map(s =>
                              s.id === slot.id
                                ? { ...s, datePreset: preset, dateRangeMode: 'preset' as const, customDateRange: { start: null, end: null } }
                                : s
                            );
                            onCompareStateChange({ ...compareState, slots: updatedSlots });
                          }}
                          onCustomDateRangeChange={(start, end) => {
                            const updatedSlots = slots.map(s =>
                              s.id === slot.id
                                ? { ...s, datePreset: 'custom' as const, dateRangeMode: 'custom' as const, customDateRange: { start, end } }
                                : s
                            );
                            onCompareStateChange({ ...compareState, slots: updatedSlots });
                          }}
                          disabled={slot.groupIds.length === 0}
                          groupLabel={groupLabel}
                          previousDateSettings={slot.previousDateSettings}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <div className="border-t border-border" />

      {/* Metric Selector - Collapsible */}
      <div className="space-y-3">
        <button
          onClick={() => setMetricsExpanded(!metricsExpanded)}
          className="w-full flex items-center justify-between hover:bg-muted/30 rounded px-1 py-0.5 -mx-1 transition-colors"
        >
          <div className="flex items-center gap-1.5">
            {metricsExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <Label className="text-xs text-muted-foreground uppercase tracking-wider cursor-pointer">
              Metrics
            </Label>
          </div>
          <span className="text-xs text-muted-foreground">
            {compareState.enabledMetrics.length} selected
          </span>
        </button>

        {metricsExpanded && (
              <MetricChipSelector
            enabledMetrics={compareState.enabledMetrics}
            onMetricsChange={handleMetricsChange}
            disabled={slots.length === 0}
            inactive={!activeSections.includes('chart')}
          />
        )}
      </div>
    </div>
  );
}
